import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, EASE } from "@/components/reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1598203637341-69d7580a1507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHw0fHxjb250YWluZXIlMjBwb3J0JTIwY29udGFpbmVyJTIwc2hpcCUyMGNhcmdvJTIwaW5mcmFzdHJ1Y3R1cmUlMjBtYXJpdGltZSUyMHNoaXBwaW5nJTIwbG9naXN0aWNzJTIwZ3JlZW4lMjBlbmVyZ3klMjB0cmFuc2l0aW9uJTIwdGVybWluYWwlMjB3YXJlaG91c2UlMjBwb3J0JTIwY3JhbmV8ZW58MHx8fHwxNzkxNTMyMjQyfDA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
    const imgWrap = useRef(null);
    const { scrollYProgress } = useScroll({
        target: imgWrap,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section className="relative flex min-h-screen items-center overflow-hidden pt-[72px]">
            <div
                className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(45,62,46,0.06)_1px,transparent_1px)] [background-size:clamp(90px,12vw,160px)_100%]"
                aria-hidden="true"
            />

            <div className="container-x relative grid grid-cols-1 items-center gap-14 py-16 lg:grid-cols-12 lg:gap-10 lg:py-24">
                <div className="lg:col-span-7">
                    <FadeUp delay={0.9}>
                        <p className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-olive">
                            <span className="inline-block h-px w-10 bg-olive" />
                            Strategy &amp; capital advisory
                        </p>
                    </FadeUp>

                    <MaskedLines
                        className="font-serif text-[2.55rem] leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.1rem]"
                        delay={0.15}
                        lines={[
                            "Strategy and capital",
                            "for the infrastructure",
                            <>
                                that <em className="italic text-olive">moves</em> the
                                world.
                            </>,
                        ]}
                    />

                    <FadeUp delay={1.0}>
                        <p className="mt-8 max-w-xl text-lg leading-relaxed text-inksoft sm:text-xl">
                            Advising governments, investors, operators and innovation
                            hubs on infrastructure worldwide — from ports and shipping
                            corridors to the energy transition.
                        </p>
                    </FadeUp>

                    <FadeUp delay={1.15}>
                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <button
                                data-testid="hero-primary-cta"
                                onClick={() => scrollToId("contact")}
                                className="group flex items-center gap-3 rounded-full bg-olive px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-olive-deep"
                            >
                                Request an introduction
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </button>
                            <button
                                data-testid="hero-secondary-cta"
                                onClick={() => scrollToId("what-we-do")}
                                className="group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-ink"
                            >
                                What we do
                                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/25 transition-colors duration-300 group-hover:border-olive group-hover:bg-olive group-hover:text-paper">
                                    <ArrowDown className="h-3.5 w-3.5" />
                                </span>
                            </button>
                        </div>
                    </FadeUp>

                </div>

                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ clipPath: "inset(100% 0 0 0)", opacity: 0 }}
                        animate={{ clipPath: "inset(0% 0 0 0)", opacity: 1 }}
                        transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
                        className="group relative"
                    >
                        <div
                            className="absolute inset-0 translate-x-3 translate-y-3 border border-olive/40"
                            aria-hidden="true"
                        />
                        <div
                            ref={imgWrap}
                            data-testid="hero-image"
                            className="relative aspect-[4/5] overflow-hidden"
                        >
                            <motion.img
                                src={HERO_IMG}
                                alt="Container terminal at a global port"
                                style={{ y }}
                                className="h-[112%] w-full object-cover"
                            />
                            <div
                                className="absolute inset-0 bg-olive/10 mix-blend-multiply"
                                aria-hidden="true"
                            />
                        </div>
                        <p className="absolute bottom-6 left-0 bg-paper px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-inksoft">
                            Global trade corridors
                        </p>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-3 lg:flex"
                aria-hidden="true"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-inkmut">
                    Scroll
                </span>
                <motion.span
                    animate={{ y: [0, 6, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    className="block h-8 w-px bg-ink/40"
                />
            </motion.div>
        </section>
    );
};

export default Hero;
